import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, ShieldCheck, FileCheck, Truck, Flame, BadgePercent, Star } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Seo } from "../components/Seo";
import { useCart } from "../cart/CartContext";
import { API } from "../config";

export const OFFER_COUPON = "FIRSTPHUDG";
export const OFFER_KEY = "phudg_coupon";

const OfferCard = ({ product, index }) => {
  const { add } = useCart();
  const navigate = useNavigate();
  const size = product.sizes?.find((s) => s.price === product.price) || product.sizes?.find((s) => s.price);
  if (!size) return null;

  const addToCart = (goToCart) => {
    add({ slug: product.slug, name: product.name, image: product.cart_image || product.image, size: size.label, price: size.price });
    toast.success(`${product.name} (${size.label}) added to cart`);
    if (goToCart) navigate("/cart");
  };

  return (
    <motion.article
      data-testid={`offer-card-${product.slug}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
      className="group rounded-3xl border border-ink/10 bg-white overflow-hidden hover:shadow-xl hover:shadow-ink/5 transition-shadow duration-500"
    >
      <div className="overflow-hidden relative">
        <img
          src={product.image}
          alt={`${product.name} — PhudG`}
          loading="lazy"
          className="w-full aspect-[4/5] object-cover transition-transform duration-700 group-hover:scale-105"
        />
        {product.combo && (
          <span className="absolute top-4 left-4 rounded-full bg-gold px-3 py-1 text-[10px] tracking-[0.2em] uppercase text-ink font-medium">Festive Combo</span>
        )}
      </div>
      <div className="p-6">
        <p className="text-[10px] tracking-[0.3em] uppercase text-amberbrand mb-1.5">{product.tagline}</p>
        <h3 className="font-serif text-2xl text-ink mb-1">{product.name}</h3>
        <p className="text-xs text-ink/45 font-light mb-4">{size.label}{product.combo ? " · gift-ready box" : ""}</p>
        <div className="flex items-center justify-between gap-3">
          <p className="font-serif text-2xl text-ink">₹{size.price}</p>
          <div className="flex gap-2">
            <button
              data-testid={`offer-add-${product.slug}`}
              onClick={() => addToCart(false)}
              className="flex items-center gap-1.5 rounded-full border border-ink/15 px-4 py-2.5 text-xs tracking-widest uppercase text-ink/70 hover:border-gold hover:text-gold transition-colors duration-300"
            >
              <ShoppingCart size={13} /> Add
            </button>
            <button
              data-testid={`offer-buy-${product.slug}`}
              onClick={() => addToCart(true)}
              className="rounded-full bg-ink px-4 py-2.5 text-xs tracking-widest uppercase text-cream hover:bg-gold hover:text-ink transition-colors duration-300"
            >
              Buy Now
            </button>
          </div>
        </div>
      </div>
    </motion.article>
  );
};

export default function OfferPage() {
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    localStorage.setItem(OFFER_KEY, OFFER_COUPON);
    axios.get(`${API}/products`).then((res) => {
      setProducts(res.data.filter((p) => p.status === "available"));
    }).catch(() => {});
    axios.get(`${API}/reviews`).then((res) => setReviews((res.data || []).slice(0, 3))).catch(() => {});
  }, []);

  const scrollToShop = () => document.getElementById("offer-shop")?.scrollIntoView({ behavior: "smooth" });

  return (
    <main data-testid="offer-page" className="min-h-screen bg-cream text-ink">
      <Seo
        title="Get 10% Off Your First Order — PhudG Cold-Pressed Oils"
        description="First order offer: 10% off with FIRSTPHUDG, free delivery, and bundle savings up to 15% on wood-pressed oils."
        path="/offer"
        noindex
      />
      <Header />

      {/* Offer hero */}
      <section data-testid="offer-hero" className="bg-ink grain text-cream pt-40 pb-20 md:pt-48 md:pb-24">
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <p className="text-xs tracking-[0.4em] uppercase text-gold mb-5">Welcome Offer</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] mb-6">
            Get <span className="italic text-gold">10% OFF</span> your first PhudG order
          </h1>
          <p className="text-sm md:text-base font-light text-cream/60 max-w-xl mx-auto mb-8 leading-relaxed">
            Wood-pressed, cold-pressed oils delivered free to your door. Your code{" "}
            <span className="text-gold font-medium tracking-[0.2em]">FIRSTPHUDG</span> is already applied — just add your oils and check out.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mb-10 text-xs tracking-widest uppercase text-cream/70">
            <span className="flex items-center gap-2 rounded-full border border-cream/20 px-4 py-2"><BadgePercent size={14} className="text-gold" /> FIRSTPHUDG auto-applied</span>
            <span className="flex items-center gap-2 rounded-full border border-cream/20 px-4 py-2"><Truck size={14} className="text-gold" /> Free delivery</span>
            <span className="flex items-center gap-2 rounded-full border border-cream/20 px-4 py-2"><BadgePercent size={14} className="text-gold" /> Bundle 2+ 1L: −12% · 4+: −15%</span>
            <span className="flex items-center gap-2 rounded-full border border-cream/20 px-4 py-2"><BadgePercent size={14} className="text-gold" /> Extra 5% off on prepaid</span>
          </div>
          <motion.button
            data-testid="offer-hero-shop-btn"
            onClick={scrollToShop}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            className="rounded-full bg-gold px-10 py-4 text-sm font-medium text-ink hover:bg-cream transition-colors duration-300"
          >
            Shop the Oils — Claim 10% Off
          </motion.button>
        </div>
      </section>

      {/* All products */}
      <section id="offer-shop" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-xs tracking-[0.4em] uppercase text-amberbrand mb-4 text-center">The Full Range</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tighter text-center mb-4">Every oil we press.</h2>
          <p className="text-sm font-light text-ink/55 text-center max-w-lg mx-auto mb-14">
            Add two or more one-litre bottles and bundle savings apply automatically at checkout.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p, i) => (
              <OfferCard key={p.slug} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="bg-ink grain text-cream py-16 md:py-20">
        <div className="relative z-10 max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { icon: Flame, title: "Wood-Pressed", desc: "Slow kolhu pressing, never heated" },
            { icon: ShieldCheck, title: "Below 45°C", desc: "Nutrients and flavour intact" },
            { icon: FileCheck, title: "Lab-Tested", desc: "Every batch, FSSAI licensed" },
            { icon: Truck, title: "Free Delivery", desc: "On every order, across India" },
          ].map((t) => (
            <div key={t.title}>
              <t.icon size={22} className="mx-auto text-gold mb-3" />
              <p className="text-sm text-cream mb-1">{t.title}</p>
              <p className="text-xs font-light text-cream/50">{t.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Reviews */}
      {reviews.length > 0 && (
        <section className="py-20 md:py-24">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="font-serif text-3xl md:text-4xl tracking-tighter text-center mb-12">Kitchens that switched.</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {reviews.map((r, i) => (
                <div key={i} data-testid={`offer-review-${i}`} className="rounded-3xl border border-ink/10 bg-white p-6">
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: r.rating || 5 }).map((_, s) => (
                      <Star key={s} size={13} className="fill-gold text-gold" />
                    ))}
                  </div>
                  <p className="text-sm font-light text-ink/70 leading-relaxed mb-4">"{r.comment}"</p>
                  <p className="text-xs tracking-[0.2em] uppercase text-ink/40">{r.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="pb-24 pt-4 text-center px-6">
        <h2 className="font-serif text-3xl md:text-4xl tracking-tighter mb-6">
          Your first order is <span className="italic text-amberbrand">10% off.</span>
        </h2>
        <motion.button
          data-testid="offer-bottom-shop-btn"
          onClick={scrollToShop}
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.97 }}
          className="rounded-full bg-ink px-10 py-4 text-sm text-cream hover:bg-gold hover:text-ink transition-colors duration-300"
        >
          Shop Now — FIRSTPHUDG Applied
        </motion.button>
      </section>

      <Footer />
    </main>
  );
}
